import { getSupabase } from './supabase';
import {
  ensureIdentity,
  getIdentity,
  normalizeHomeworld,
  resetGuestIdentityLocal,
  setHandleLocal,
  setHomeworldLocal,
  setUserIdLocal,
} from './identity';
import { isHandleAllowed } from './handles';
import { clearAuthPending, markAuthPending } from './authRouting';
import { authRedirectUrl } from './authRedirect';

export interface AuthResult {
  ok: boolean;
  error?: string;
}

export async function sendMagicLink(email: string): Promise<AuthResult> {
  const sb = getSupabase();
  if (!sb) return { ok: false, error: 'Backend not configured' };
  markAuthPending('pilot');
  const { error } = await sb.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: authRedirectUrl('/') },
  });
  if (error) {
    clearAuthPending('pilot');
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/** After magic-link redirect, attach this device + its prior anonymous laps to the user. */
export async function attachClaimedDevice(handle?: string, homeworld?: string): Promise<AuthResult> {
  const sb = getSupabase();
  if (!sb) return { ok: false, error: 'Backend not configured' };
  const { data: { user } } = await sb.auth.getUser();
  const id = getIdentity();
  if (!user || !id) return { ok: false, error: 'No active session or identity' };

  const chosenHandle = (handle ?? id.handle).trim();
  const chosenHomeworld = normalizeHomeworld(homeworld ?? id.homeworld);
  return savePilotProfile(chosenHandle, chosenHomeworld);
}

export async function setUserHandle(handle: string): Promise<AuthResult> {
  const id = await ensureIdentity();
  return savePilotProfile(handle, id.homeworld);
}

export async function savePilotProfile(handle: string, homeworld: string): Promise<AuthResult> {
  if (!isHandleAllowed(handle)) return { ok: false, error: 'Handle not allowed' };
  const trimmed = handle.trim();
  if (trimmed.length < 3 || trimmed.length > 32) {
    return { ok: false, error: 'Handle must be 3-32 characters' };
  }
  const normalizedHomeworld = normalizeHomeworld(homeworld);
  const id = await ensureIdentity();

  const sb = getSupabase();
  if (!sb) {
    setHandleLocal(trimmed);
    setHomeworldLocal(normalizedHomeworld);
    return { ok: true };
  }

  const { data, error } = await sb.rpc('tst_save_pilot_profile', {
    p_device_id: id.deviceId,
    p_device_secret: id.deviceSecret,
    p_handle: trimmed,
    p_homeworld: normalizedHomeworld,
  });
  if (error) {
    const fallback = await savePilotProfileLegacy(trimmed);
    if (!fallback.ok) return fallback;
    setHomeworldLocal(normalizedHomeworld);
    return { ok: true };
  }

  const row = Array.isArray(data) ? data[0] : data;
  if (row && typeof row === 'object') {
    const saved = row as { handle?: unknown; homeworld?: unknown; user_id?: unknown };
    if (typeof saved.handle === 'string') setHandleLocal(saved.handle);
    else setHandleLocal(trimmed);
    if (typeof saved.homeworld === 'string') setHomeworldLocal(saved.homeworld);
    else setHomeworldLocal(normalizedHomeworld);
    setUserIdLocal(id.authUserId && typeof saved.user_id === 'string' ? saved.user_id : null);
  } else {
    setHandleLocal(trimmed);
    setHomeworldLocal(normalizedHomeworld);
  }
  return { ok: true };
}

export async function isSignedIn(): Promise<boolean> {
  const sb = getSupabase();
  if (!sb) return false;
  const { data: { user } } = await sb.auth.getUser();
  return Boolean(user);
}

export async function getCurrentUserEmail(): Promise<string | null> {
  const sb = getSupabase();
  if (!sb) return null;
  const { data: { user } } = await sb.auth.getUser();
  return user?.email ?? null;
}

export async function signOut(): Promise<void> {
  await getSupabase()?.auth.signOut();
  resetGuestIdentityLocal();
  await ensureIdentity();
}

async function savePilotProfileLegacy(handle: string): Promise<AuthResult> {
  const sb = getSupabase();
  const id = getIdentity();
  if (!sb || !id) return { ok: false, error: 'Backend not configured' };

  const { data: { user } } = await sb.auth.getUser();
  if (user) {
    const claimed = await sb.rpc('tst_claim_device', {
      p_device_id: id.deviceId,
      p_device_secret: id.deviceSecret,
      p_handle: handle,
    });
    if (claimed.error) return { ok: false, error: claimed.error.message };
    const row = Array.isArray(claimed.data) ? claimed.data[0] : claimed.data;
    if (row && typeof row === 'object') {
      const saved = row as { handle?: unknown; user_id?: unknown };
      if (typeof saved.handle === 'string') setHandleLocal(saved.handle);
      if (typeof saved.user_id === 'string') setUserIdLocal(saved.user_id);
    }
    return { ok: true };
  }

  const upserted = await sb.rpc('tst_upsert_device', {
    p_device_id: id.deviceId,
    p_handle: handle,
    p_device_secret: id.deviceSecret,
  });
  if (upserted.error) return { ok: false, error: upserted.error.message };
  const row = Array.isArray(upserted.data) ? upserted.data[0] : upserted.data;
  if (row && typeof row === 'object') {
    const saved = row as { handle?: unknown; user_id?: unknown };
    if (typeof saved.handle === 'string') setHandleLocal(saved.handle);
    setUserIdLocal(null);
  } else {
    setHandleLocal(handle);
  }
  return { ok: true };
}
